import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { isBrandingOnlyBrand } from '../utils/brandingClassifier';
import { Calendar, Save, Edit3, X, TrendingUp, TrendingDown, Loader2 } from 'lucide-react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const API = 'https://api.glowytics.com';

// Yataş grubu markaları (yatasCampaignNameFilter.js ile aynı)
const YATAS_GROUP_BRAND_IDS = new Set([
  '44e35dfe-0186-495d-a0cc-db5e7e459ab7', // Yataş Bedding
  'dfcf54dd-b00d-434a-be1e-27b6484848eb', // Divanev
  '7fc8e68d-f2ba-4101-95f7-acece386ab8f', // Puffy
  'ccb2364b-e4e1-4f7f-944a-44cc9a839c6e', // EnzaHome
]);

// "Tümü branding" markası mı? (Divanev — custom-report'larla AYNI liste,
// brandingClassifier.BRANDING_ONLY_BRAND_IDS; backend ALL_BRANDING_BRAND_IDS ile birebir.)
// Bu markalarda kampanya adında branding/performans etiketi yok ve olmayacak: medya
// planının tamamı branding → harcama/gösterim/izlenmenin tamamı Branding kovasına yazılır,
// Performans kartları gösterilmez.

// split: bu metrik Branding/Performans olarak ayrıştırılabilir (kampanya adına göre).
// Ciro artık ayrılabiliyor — kaynak ga_traffic_source_raw, kampanya adı taşıyor.
// Trafik/kullanıcı/bounce hâlâ GA4-blended → bölünemez, split:false.
//
// NOT: Bu liste hem KPI kartlarını hem HEDEF GİRİŞ sütunlarını besliyor. Buraya
// eklenen her anahtarın backend METRICS'inde bir hedef kolonu olmalı — türetilmiş
// (hedefi olmayan) kartlar için aşağıdaki CARD_GROUPS kullanılır.
const METRICS = [
  { key: 'sessions',     label: 'Trafik',    sub: 'Session',     kind: 'int',   split: false },
  { key: 'users',        label: 'Kullanıcı', sub: 'User',        kind: 'int',   split: false },
  { key: 'bounce_rate',  label: 'Bounce Rate', sub: 'Hemen çıkma oranı', kind: 'pct', split: false, lowerIsBetter: true },
  { key: 'impressions',  label: 'Gösterim',  sub: 'Impression',  kind: 'int',   split: true  },
  { key: 'views',        label: 'İzlenme',   sub: 'View',        kind: 'int',   split: true  },
  { key: 'transactions', label: 'Sipariş',   sub: 'Transaction', kind: 'int',   split: false },
  { key: 'revenue',      label: 'Ciro',      sub: 'Revenue',     kind: 'money', split: true  },
  { key: 'cost',         label: 'Bütçe',     sub: 'Cost',        kind: 'money', split: true  },
  { key: 'roas',         label: 'ROAS',      sub: 'Toplam ciro / performans harcaması', kind: 'ratio', split: false },
];

// Ayrım hedefi anahtarları (backend SPLIT_TARGETS ile birebir)
const SPLIT_KEYS = ['cost', 'impressions', 'views', 'revenue'];

// Türetilmiş kartlar — kendi hedef kolonu YOK, ayrım hedefinden okurlar.
// (Hedef düzenleyicide zaten "Ciro · Pf" / "Bütçe · Pf" sütunları olarak varlar.)
const DERIVED_CARDS = {
  revenue_performans: { key: 'revenue_performans', label: 'Ciro (Perf.)',  sub: 'Performans kampanyaları', kind: 'money', split: false },
  cost_performans:    { key: 'cost_performans',    label: 'Performans Bütçesi', sub: 'Performans kampanyaları', kind: 'money', split: false },
  // "Tümü branding" markalarında Performans Bütçesi kartının yerini alır
  cost_branding:      { key: 'cost_branding',      label: 'Branding Bütçesi',   sub: 'Tüm reklam harcaması',   kind: 'money', split: false },
};

// KPI ızgarası — ilişkili metrikler yan yana dursun diye gruplu.
//
// 14 Ağu 2026 (Yataş talebi): ayrı "Performans Kanalı" bölümü kaldırıldı. Satış & Ciro'daki
// bütçe kartı toplam harcama yerine PERFORMANS harcamasını gösteriyor ve ROAS = toplam ciro /
// performans harcaması (backend'de de böyle hesaplanıyor). Toplam harcama kaybolmadı —
// üstteki "Bütçe — Branding vs Performans" bloğunda ikisi de duruyor.
const CARD_GROUPS = [
  { title: 'Trafik & Etkileşim', keys: ['sessions', 'users', 'bounce_rate'] },
  { title: 'Reklam Erişimi',     keys: ['impressions', 'views'] },
  // Bütçe ve ROAS kampanya adındaki "performance/performans" etiketine dayanıyor:
  // etiket kullanmayan markalarda ikisi de 0 çıkar — uyarı gösterilir. "Tümü branding"
  // markasında (Divanev) bunun yerine Branding Bütçesi kartı + kural notu gösterilir.
  { title: 'Satış & Ciro', keys: ['transactions', 'revenue', 'cost_performans', 'roas'],
    requiresSplit: true },
];

const MONTHS = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];
const MONTHS_SHORT = ['Oca','Şub','Mar','Nis','May','Haz','Tem','Ağu','Eyl','Eki','Kas','Ara'];

// ---------------------------------------------------------------------------
// Formatlama
// ---------------------------------------------------------------------------
const trInt = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 });

function fmtValue(v, kind) {
  if (v === null || v === undefined || isNaN(v)) return '—';
  if (kind === 'money') return '₺' + trInt.format(Math.round(v));
  if (kind === 'ratio') return (Math.round(v * 100) / 100).toLocaleString('tr-TR') + 'x';
  if (kind === 'pct') return '%' + (Math.round(v * 10) / 10).toLocaleString('tr-TR');
  return trInt.format(Math.round(v));
}

// kompakt değer (2.5M, 120K) — kartlarda yer kazanmak için
function fmtCompact(v, kind) {
  if (v == null || isNaN(v)) return '—';
  if (kind === 'ratio') return (Math.round(v * 100) / 100).toLocaleString('tr-TR') + 'x';
  if (kind === 'pct') return '%' + (Math.round(v * 10) / 10).toLocaleString('tr-TR');
  const a = Math.abs(v);
  const pre = kind === 'money' ? '₺' : '';
  if (a >= 1_000_000) return pre + (v / 1_000_000).toFixed(1) + 'M';
  if (a >= 1_000) return pre + (v / 1_000).toFixed(1) + 'K';
  return pre + String(Math.round(v));
}

// Gerçekleşme oranı. Bounce gibi "düşük iyi" metriklerde oran ters çevrilir:
// hedef %35, gerçekleşen %30 ise başarı %117 olmalı — %86 değil.
function rate(actual, target, lowerIsBetter = false) {
  if (target === null || target === undefined || target === 0) return null;
  if (lowerIsBetter) return actual > 0 ? (target / actual) * 100 : null;
  return (actual / target) * 100;
}

function statusOf(r) {
  if (r === null) return { text: 'Hedef yok', cls: 'bg-gray-100 text-gray-500', dot: 'bg-gray-300' };
  if (r >= 100)  return { text: 'Ulaşıldı', cls: 'bg-green-100 text-green-700', dot: 'bg-green-500' };
  if (r >= 85)   return { text: 'Yakın',    cls: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' };
  if (r >= 60)   return { text: 'Geride',   cls: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500' };
  return { text: 'Risk', cls: 'bg-red-100 text-red-700', dot: 'bg-red-500' };
}

// Bir metriğin seçilen aydaki değerlerini + ayrımı çöz
function resolveMetric(metric, months, selMonth) {
  const sel = months.find((m) => m.month === selMonth) || { actuals: {}, targets: {} };
  const actual = sel.actuals?.[metric.key] ?? 0;
  let target = sel.targets?.[metric.key] ?? null;

  let split = null;
  if (metric.split) {
    const brA = sel.actuals?.[`${metric.key}_branding`] ?? 0;
    const pfA = sel.actuals?.[`${metric.key}_performans`] ?? 0;
    const digerA = sel.actuals?.[`${metric.key}_diger`] ?? Math.max(0, actual - brA - pfA);
    const brT = sel.targets?.[`${metric.key}_branding`] ?? null;
    const pfT = sel.targets?.[`${metric.key}_performans`] ?? null;
    // Toplam hedef girilmemişse Br+Pf'den türet
    if (target == null && (brT != null || pfT != null)) target = (brT || 0) + (pfT || 0);
    split = { brA, pfA, digerA, brT, pfT };
  }
  return { actual, target, split };
}

// ---------------------------------------------------------------------------
// Kompakt KPI kartı (ızgara) — tıklayınca detay açılır
// ---------------------------------------------------------------------------
function KpiCard({ metric, months, selMonth, onOpen }) {
  const { actual, target, split } = resolveMetric(metric, months, selMonth);
  const r = rate(actual, target, metric.lowerIsBetter);
  const st = statusOf(r);
  const diff = target != null ? actual - target : null;
  // "Düşük iyi" metriklerde hedefin ALTINDA kalmak başarıdır → renk ters
  const diffGood = diff == null ? null : (metric.lowerIsBetter ? diff <= 0 : diff >= 0);

  const spark = months
    .filter((m) => m.month <= selMonth)
    .map((m) => ({ ay: MONTHS_SHORT[m.month - 1], v: m.actuals?.[metric.key] ?? 0 }));

  // Ayrım oran çubuğu
  let brPct = 0, pfPct = 0;
  if (split) {
    const tot = split.brA + split.pfA + split.digerA;
    if (tot > 0) { brPct = (split.brA / tot) * 100; pfPct = (split.pfA / tot) * 100; }
  }

  return (
    <button
      onClick={onOpen}
      className="text-left bg-white rounded-xl border border-gray-200 shadow-sm p-4 hover:shadow-md hover:border-blue-300 transition-all flex flex-col"
    >
      <div className="flex items-start justify-between mb-1.5">
        <div>
          <div className="text-sm font-semibold text-gray-700">{metric.label}</div>
          <div className="text-[10px] text-gray-400">{metric.sub}</div>
        </div>
        <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full ${st.cls}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
          {r != null ? r.toFixed(0) + '%' : st.text}
        </span>
      </div>

      <div className="flex items-end justify-between">
        <div className="text-xl font-bold text-gray-900 leading-tight">{fmtValue(actual, metric.kind)}</div>
        <div className="text-[10px] text-gray-400 text-right">
          Hedef<br /><span className="text-gray-600 font-medium">{fmtCompact(target, metric.kind)}</span>
        </div>
      </div>

      {diff != null && (
        <div className={`text-[10px] mt-0.5 flex items-center gap-0.5 ${diffGood ? 'text-green-600' : 'text-red-500'}`}>
          {diff < 0 ? <TrendingDown size={11} /> : <TrendingUp size={11} />}
          {fmtCompact(Math.abs(diff), metric.kind)} {diff < 0 ? 'eksik' : 'fazla'}
        </div>
      )}

      {/* Ayrım (Branding/Performans) veya sparkline */}
      {split ? (
        <div className="mt-2.5">
          <div className="flex h-1.5 rounded-full overflow-hidden bg-gray-100">
            <div className="bg-indigo-500" style={{ width: `${brPct}%` }} />
            <div className="bg-emerald-500" style={{ width: `${pfPct}%` }} />
          </div>
          <div className="flex justify-between text-[10px] mt-1">
            <span className="text-indigo-600 font-medium">Br {fmtCompact(split.brA, metric.kind)}</span>
            <span className="text-emerald-600 font-medium">Pf {fmtCompact(split.pfA, metric.kind)}</span>
          </div>
        </div>
      ) : (
        <div className="mt-2 h-9">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={spark} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
              <Line type="monotone" dataKey="v" stroke="#2563eb" strokeWidth={1.5} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Detay modal — büyük Hedef vs Gerçekleşen grafiği (+ ayrım grafiği)
// ---------------------------------------------------------------------------
function MetricDetail({ metric, months, selMonth, year, onClose }) {
  const chartData = months
    .filter((m) => m.month <= selMonth)
    .map((m) => ({
      ay: MONTHS_SHORT[m.month - 1],
      Hedef: m.targets?.[metric.key] ?? null,
      Gerçekleşen: m.actuals?.[metric.key] ?? 0,
      oran: rate(m.actuals?.[metric.key] ?? 0, m.targets?.[metric.key] ?? null, metric.lowerIsBetter),
      Branding: metric.split ? (m.actuals?.[`${metric.key}_branding`] ?? 0) : null,
      Performans: metric.split ? (m.actuals?.[`${metric.key}_performans`] ?? 0) : null,
    }));

  const { actual, target, split } = resolveMetric(metric, months, selMonth);
  const r = rate(actual, target, metric.lowerIsBetter);
  const st = statusOf(r);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">{metric.label} <span className="text-xs font-normal text-gray-400">{metric.sub}</span></h3>
            <span className="text-xs text-gray-500">{MONTHS[selMonth - 1]} {year}</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <div className="p-4">
          {/* Özet kartlar */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="rounded-lg bg-blue-50 border border-blue-100 p-3">
              <div className="text-xs text-gray-500">Hedef</div>
              <div className="text-xl font-bold text-gray-800">{fmtValue(target, metric.kind)}</div>
            </div>
            <div className="rounded-lg bg-blue-50 border border-blue-100 p-3">
              <div className="text-xs text-gray-500">Gerçekleşen</div>
              <div className="text-xl font-bold text-gray-800">{fmtValue(actual, metric.kind)}</div>
            </div>
            <div className="rounded-lg bg-amber-50 border border-amber-100 p-3">
              <div className="text-xs text-gray-500">Oran</div>
              <div className="text-xl font-bold text-gray-800">{r != null ? r.toFixed(1) + '%' : '—'}</div>
              <span className={`inline-block text-[10px] px-2 py-0.5 rounded mt-0.5 ${st.cls}`}>{st.text}</span>
            </div>
          </div>

          {/* Ayrım özeti */}
          {split && (
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-3">
                <div className="text-xs text-indigo-600 font-medium">Branding</div>
                <div className="text-lg font-bold text-gray-800">{fmtValue(split.brA, metric.kind)}</div>
                {split.brT != null && <div className="text-[10px] text-gray-500">Hedef {fmtValue(split.brT, metric.kind)}</div>}
              </div>
              <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-3">
                <div className="text-xs text-emerald-600 font-medium">Performans</div>
                <div className="text-lg font-bold text-gray-800">{fmtValue(split.pfA, metric.kind)}</div>
                {split.pfT != null && <div className="text-[10px] text-gray-500">Hedef {fmtValue(split.pfT, metric.kind)}</div>}
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="text-xs text-gray-500 font-medium">Diğer</div>
                <div className="text-lg font-bold text-gray-800">{fmtValue(split.digerA, metric.kind)}</div>
              </div>
            </div>
          )}

          {/* Hedef vs Gerçekleşen */}
          <div className="text-xs font-medium text-gray-500 mb-1">Hedef vs Gerçekleşen</div>
          <div style={{ width: '100%', height: 240 }}>
            <ResponsiveContainer>
              <ComposedChart data={chartData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="ay" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="left" tickFormatter={(v) => fmtCompact(v, metric.kind === 'money' ? 'int' : metric.kind)} tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => v + '%'} tick={{ fontSize: 11, fill: '#16a34a' }} domain={[0, 'auto']} />
                <Tooltip formatter={(val, name) => (name === 'Gerçekleşme %' ? [val != null ? val.toFixed(1) + '%' : '—', name] : [fmtValue(val, metric.kind), name])} />
                <Legend />
                <Bar yAxisId="left" dataKey="Hedef" fill="#bfdbfe" radius={[3, 3, 0, 0]} />
                <Bar yAxisId="left" dataKey="Gerçekleşen" fill="#2563eb" radius={[3, 3, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="oran" name="Gerçekleşme %" stroke="#16a34a" strokeWidth={2} dot={{ r: 3 }} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Ayrım grafiği: Branding vs Performans (yığılı) */}
          {split && (
            <>
              <div className="text-xs font-medium text-gray-500 mb-1 mt-4">Branding vs Performans (gerçekleşen)</div>
              <div style={{ width: '100%', height: 220 }}>
                <ResponsiveContainer>
                  <ComposedChart data={chartData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="ay" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={(v) => fmtCompact(v, metric.kind === 'money' ? 'int' : metric.kind)} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(val, name) => [fmtValue(val, metric.kind), name]} />
                    <Legend />
                    <Bar dataKey="Branding" stackId="s" fill="#6366f1" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="Performans" stackId="s" fill="#10b981" radius={[3, 3, 0, 0]} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hedef düzenleme grid'i (12 ay × KPI; split metriklerde Br/Pf alt-alanları)
// ---------------------------------------------------------------------------
function TargetEditor({ year, brandId, onClose, onSaved }) {
  const [grid, setGrid] = useState(null); // {month: {key: value}}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(new Set());

  // "Tümü branding" markasında Pf hedefi girilecek bir şey yok — o sütunlar gizlenir.
  const allBranding = isBrandingOnlyBrand(brandId);

  // Editor sütun tanımları: her metrik + split ise Br/Pf ek anahtarlar
  const COLUMNS = useMemo(() => {
    const cols = [];
    METRICS.forEach((m) => {
      cols.push({ key: m.key, label: m.label, kind: m.kind, group: m.label });
      if (m.split) {
        cols.push({ key: `${m.key}_branding`, label: `${m.label} · Br`, kind: m.kind, group: m.label, sub: true });
        if (!allBranding) {
          cols.push({ key: `${m.key}_performans`, label: `${m.label} · Pf`, kind: m.kind, group: m.label, sub: true });
        }
      }
    });
    return cols;
  }, [allBranding]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/yatas-kpi/targets?year=${year}`, {
        credentials: 'include', headers: { 'X-Brand-ID': brandId },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const g = {};
      for (let m = 1; m <= 12; m++) {
        const src = json.targets?.[m] || {};
        g[m] = {};
        COLUMNS.forEach((c) => { g[m][c.key] = src[c.key] != null ? String(src[c.key]) : ''; });
      }
      setGrid(g);
      setDirty(new Set());
    } catch (e) {
      toast.error('Hedefler yüklenemedi: ' + e.message);
    } finally { setLoading(false); }
  }, [year, brandId, COLUMNS]);

  useEffect(() => { load(); }, [load]);

  const setCell = (m, key, val) => {
    setGrid((prev) => ({ ...prev, [m]: { ...prev[m], [key]: val } }));
    setDirty((prev) => new Set(prev).add(m));
  };

  const save = async () => {
    if (dirty.size === 0) { toast('Değişiklik yok'); return; }
    setSaving(true);
    try {
      for (const m of dirty) {
        const targets = {};
        COLUMNS.forEach((c) => { targets[c.key] = grid[m][c.key]; });
        const res = await fetch(`${API}/api/yatas-kpi/targets`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
          body: JSON.stringify({ year, month: m, targets }),
        });
        if (!res.ok) throw new Error(`Ay ${m}: HTTP ${res.status}`);
      }
      toast.success('Hedefler kaydedildi');
      setDirty(new Set());
      onSaved?.();
    } catch (e) {
      toast.error('Kaydetme hatası: ' + e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-[95vw] max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="text-lg font-semibold">Hedefleri Düzenle — {year}</h3>
            <p className="text-[11px] text-gray-400">
              {allBranding
                ? 'Bu markanın tüm reklamları Branding: yalnız Branding (Br) hedefi girilir, Br boş bırakılırsa toplam hedef Branding sayılır.'
                : 'Bütçe · Gösterim · İzlenme için Branding (Br) ve Performans (Pf) hedefi ayrı girilebilir.'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <div className="p-4 overflow-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <Loader2 className="animate-spin mr-2" /> Yükleniyor…
            </div>
          ) : (
            <table className="text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left p-2 sticky left-0 bg-gray-50 z-10">Ay</th>
                  {COLUMNS.map((c) => (
                    <th key={c.key} className={`p-2 text-right whitespace-nowrap ${c.sub ? 'text-[10px] text-gray-400 font-normal' : ''}`}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <tr key={m} className={`border-b ${dirty.has(m) ? 'bg-amber-50' : ''}`}>
                    <td className="p-2 font-medium sticky left-0 bg-inherit z-10">{MONTHS[m - 1]}</td>
                    {COLUMNS.map((c) => (
                      <td key={c.key} className="p-1">
                        <input
                          type="number"
                          step={c.kind === 'ratio' ? '0.01' : c.kind === 'pct' ? '0.1' : '1'}
                          value={grid[m][c.key]}
                          onChange={(e) => setCell(m, c.key, e.target.value)}
                          className={`border border-gray-200 rounded px-2 py-1 text-right focus:border-blue-400 focus:outline-none ${c.sub ? 'w-20 bg-gray-50/60' : 'w-24'}`}
                          placeholder="—"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <span className="text-xs text-gray-500">
            {dirty.size > 0 ? `${dirty.size} ay düzenlendi (sarı satırlar)` : 'Değişiklik yok'}
          </span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm rounded border border-gray-300 hover:bg-gray-100">Kapat</button>
            <button onClick={save} disabled={saving || dirty.size === 0}
              className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Kaydet
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Üst blok — Bütçe: Branding vs Performans (net, göze çarpan)
// ---------------------------------------------------------------------------
function BudgetSplitCard({ title, accent, actual, target }) {
  const r = rate(actual, target);
  const st = statusOf(r);
  const diff = target != null ? actual - target : null;
  const barPct = r != null ? Math.min(100, Math.max(0, r)) : 0;
  const accents = {
    indigo:  { bar: 'bg-indigo-500',  ring: 'border-indigo-200',  chip: 'text-indigo-600',  soft: 'bg-indigo-50' },
    emerald: { bar: 'bg-emerald-500', ring: 'border-emerald-200', chip: 'text-emerald-600', soft: 'bg-emerald-50' },
  }[accent];

  return (
    <div className={`rounded-xl border ${accents.ring} bg-white shadow-sm p-5`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs font-semibold uppercase tracking-wide ${accents.chip}`}>{title}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full ${st.cls}`}>{r != null ? r.toFixed(0) + '%' : st.text}</span>
      </div>
      <div className="text-3xl font-bold text-gray-900 leading-none mb-1">{fmtValue(actual, 'money')}</div>
      <div className="text-xs text-gray-500 mb-3">
        Hedef <span className="font-medium text-gray-700">{fmtValue(target, 'money')}</span>
        {diff != null && (
          <span className={`ml-2 ${diff < 0 ? 'text-red-500' : 'text-green-600'}`}>
            {diff < 0 ? '▼' : '▲'} {fmtCompact(Math.abs(diff), 'money')} {diff < 0 ? 'eksik' : 'fazla'}
          </span>
        )}
      </div>
      <div className={`h-2 rounded-full ${accents.soft} overflow-hidden`}>
        <div className={`h-full ${accents.bar}`} style={{ width: `${barPct}%` }} />
      </div>
    </div>
  );
}

function BudgetSplitBanner({ months, selMonth, year, allBranding, onOpen }) {
  const costMetric = METRICS.find((m) => m.key === 'cost');
  const { split } = resolveMetric(costMetric, months, selMonth);
  if (!split) return null;
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-1.5">
        <h2 className="text-sm font-semibold text-gray-600">
          {allBranding ? 'Bütçe — Branding' : 'Bütçe — Branding vs Performans'}
          <span className="font-normal text-gray-400"> · {MONTHS[selMonth - 1]} {year}</span>
        </h2>
        <button onClick={onOpen} className="text-[11px] text-blue-600 hover:underline">Detay →</button>
      </div>
      {/* "Tümü branding" markasında performans kartı hep 0 olurdu — tek kart gösterilir */}
      <div className={`grid grid-cols-1 gap-3 ${allBranding ? '' : 'sm:grid-cols-2'}`}>
        <BudgetSplitCard title="Branding Bütçe" accent="indigo" actual={split.brA} target={split.brT} />
        {!allBranding && (
          <BudgetSplitCard title="Performans Bütçe" accent="emerald" actual={split.pfA} target={split.pfT} />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ana sayfa
// ---------------------------------------------------------------------------
export default function YatasKpiTargets() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id;
  const isYatas = brandId && YATAS_GROUP_BRAND_IDS.has(brandId);
  const allBranding = !!brandId && isBrandingOnlyBrand(brandId);

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [openMetric, setOpenMetric] = useState(null);

  const yearOptions = useMemo(() => {
    const y = now.getFullYear();
    return [y + 1, y, y - 1, y - 2];
  }, [now]);

  // "Tümü branding" markasında Performans Bütçesi kartı anlamsız (hep 0) — yerine
  // Branding Bütçesi konur ve "etiket yok" uyarısı gösterilmez.
  const cardGroups = useMemo(() => (
    allBranding
      ? CARD_GROUPS.map((g) => ({
          ...g,
          keys: g.keys.map((k) => (k === 'cost_performans' ? 'cost_branding' : k)),
          requiresSplit: false,
        }))
      : CARD_GROUPS
  ), [allBranding]);

  const fetchData = useCallback(async () => {
    if (!isYatas) return;
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API}/api/yatas-kpi/overview?year=${year}`, {
        credentials: 'include', headers: { 'X-Brand-ID': brandId },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message);
      toast.error('Veriler yüklenemedi: ' + e.message);
    } finally { setLoading(false); }
  }, [isYatas, brandId, year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!isYatas) {
    return (
      <div className="p-8 text-center text-gray-400 text-sm">
        Bu sayfaya erişim yetkiniz bulunmuyor.
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Başlık + control box */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Hedef vs Gerçekleşen</h1>
          <p className="text-sm text-gray-500">
            {selectedBrand?.name} — aylık KPI takibi · {allBranding
              ? 'tüm reklam harcaması Branding · ROAS = toplam ciro / toplam harcama'
              : 'Bütçe/Gösterim/İzlenme Branding-Performans ayrımlı · ROAS = toplam ciro / performans harcaması'}
          </p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm">
          <Calendar size={16} className="text-gray-400" />
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))}
            className="border-0 text-sm focus:outline-none bg-transparent">
            {MONTHS.map((mn, i) => <option key={i} value={i + 1}>{mn}</option>)}
          </select>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}
            className="border-0 text-sm focus:outline-none bg-transparent">
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={() => setEditing(true)}
            className="ml-2 flex items-center gap-1 text-sm bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700">
            <Edit3 size={14} /> Hedefleri Düzenle
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <Loader2 className="animate-spin mr-2" /> Yükleniyor…
        </div>
      )}
      {error && !loading && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">Hata: {error}</div>
      )}

      {/* Üst blok: Bütçe Branding vs Performans (net) */}
      {!loading && data && data.months && (
        <BudgetSplitBanner
          months={data.months}
          selMonth={month}
          year={year}
          allBranding={allBranding}
          onOpen={() => setOpenMetric(METRICS.find((m) => m.key === 'cost'))}
        />
      )}

      {/* Kompakt KPI ızgarası — ilişkili metrikler gruplu */}
      {!loading && data && data.months && cardGroups.map((g) => {
        // Ayrım etiketi hiç yoksa 0'lar hata gibi okunuyor — sebebini yaz.
        const noSplit = g.requiresSplit && !data.months.some(
          (m) => (m.actuals?.cost_performans ?? 0) > 0 || (m.actuals?.cost_branding ?? 0) > 0
        );
        // "Tümü branding" markasında Satış & Ciro bölümünün kuralını yaz — 0'lar hata sanılmasın
        const brandingNote = allBranding && g.title === 'Satış & Ciro';
        const noRevenue = brandingNote && !data.months.some((m) => (m.actuals?.revenue ?? 0) > 0);
        return (
        <div key={g.title} className="mb-4">
          <h2 className="text-sm font-semibold text-gray-600 mb-1.5">{g.title}</h2>
          {brandingNote && (
            <div className="mb-2 text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-2.5 py-1.5">
              Bu markada <b>performans kampanyası yok</b>: reklam harcamasının tamamı
              <b> Branding</b> kabul edilir, ROAS'ın paydası da toplam harcamadır.
              {noRevenue && <> Ayrıca GA4'ten <b>ciro/sipariş gelmiyor</b>, bu yüzden Ciro, Sipariş ve ROAS 0 görünüyor.</>}
            </div>
          )}
          {noSplit && (
            <div className="mb-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
              Bu markanın kampanya adlarında <b>branding / performans</b> etiketi yok, bu yüzden ayrım
              yapılamıyor ve <b>Performans Bütçesi</b> ile <b>ROAS</b> 0 görünüyor.
              Etiketleme yapıldığında otomatik dolar.
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {g.keys.map((k) => {
              const m = METRICS.find((x) => x.key === k) || DERIVED_CARDS[k];
              if (!m) return null;
              return (
                <KpiCard key={k} metric={m} months={data.months} selMonth={month} onOpen={() => setOpenMetric(m)} />
              );
            })}
          </div>
        </div>
        );
      })}

      {openMetric && data?.months && (
        <MetricDetail metric={openMetric} months={data.months} selMonth={month} year={year} onClose={() => setOpenMetric(null)} />
      )}

      {editing && (
        <TargetEditor
          year={year}
          brandId={brandId}
          onClose={() => setEditing(false)}
          onSaved={fetchData}
        />
      )}
    </div>
  );
}
